import { useState, useMemo } from 'react';

// KISS: 페이지네이션 훅 옵션
interface UsePaginationOptions {
  initialPageSize?: number;
  pageSizeOptions?: number[];
}

// KISS: 페이지네이션 훅 반환값
interface UsePaginationReturn<T> {
  // 현재 상태
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  
  // 페이지된 데이터
  paginatedData: T[];
  
  // 액션들
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  
  // 헬퍼 함수들
  canGoPrev: boolean;
  canGoNext: boolean;
  getPageNumbers: () => (number | string)[];
}

// KISS: 강력한 페이지네이션 훅! 💪
export function usePagination<T>(
  data: T[],
  options: UsePaginationOptions = {}
): UsePaginationReturn<T> {
  const {
    initialPageSize = 10,
    pageSizeOptions = [5, 10, 20]
  } = options;
  
  // KISS: 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  
  // KISS: 계산된 값들
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // KISS: 페이지된 데이터
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);
  
  // KISS: 페이지 변경 처리
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, page)));
  };
  
  // KISS: 페이지 크기 변경 처리
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    // 현재 첫 번째 아이템 기준으로 페이지 재계산
    const currentFirstItem = (currentPage - 1) * pageSize + 1;
    const newPage = Math.ceil(currentFirstItem / size);
    setCurrentPage(newPage);
  };
  
  // KISS: 네비게이션 함수들
  const goToFirstPage = () => handlePageChange(1);
  const goToLastPage = () => handlePageChange(totalPages);
  const goToNextPage = () => handlePageChange(currentPage + 1);
  const goToPrevPage = () => handlePageChange(currentPage - 1);
  
  // KISS: 상태 체크
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  
  // KISS: 페이지 번호 생성 (AdvancedPagination과 동일 로직)
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  return {
    // 상태
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    
    // 데이터
    paginatedData,
    
    // 액션들
    setCurrentPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPrevPage,
    
    // 헬퍼들
    canGoPrev,
    canGoNext,
    getPageNumbers
  };
}